/**
 * Community Group Detail Page
 * /community/groups/:groupId - 모임 상세 페이지
 */

import { GroupChatTab } from "@/components/community/GroupChatTab";
import { GroupMembersTab } from "@/components/community/GroupMembersTab";
import { GroupPhotosTab } from "@/components/community/GroupPhotosTab";
import { PageContainer } from "@/components/layout/PageContainer";
import Seo from "@/components/seo/Seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { buildLoginPath } from "@/lib/auth-navigation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  MessageCircle,
  Settings,
  UserPlus,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Link, useRoute } from "wouter";

interface Group {
  id: number;
  title: string;
  slug: string;
  description: string;
  category_id: number;
  category_name: string;
  category_name_ko: string;
  category_icon: string;
  city: string;
  state: string;
  suburb: string | null;
  cover_image_url: string | null;
  member_count: number;
  event_count: number;
  post_count: number;
  view_count: number;
  is_join_approval_required: number;
  max_members: number | null;
  owner_uid: string;
  created_at: string;
  nextEvent: Event | null;
}

interface Event {
  id: number;
  title: string;
  start_ts: number;
  end_ts: number;
  location_name: string | null;
  attendance_count: number;
  capacity: number | null;
}

export default function GroupDetailPage() {
  const { user } = useAuth();
  const [, params] = useRoute<{ groupId: string }>("/community/groups/:groupId");
  const groupId = params?.groupId ? parseInt(params.groupId) : null;
  const [activeTab, setActiveTab] = useState("about");

  // 그룹 상세 조회
  const { data: group, isLoading } = useQuery<Group>({
    queryKey: ["/api/community/groups", groupId],
    queryFn: async () => {
      if (!groupId) throw new Error("Invalid group ID");
      const response = await fetch(`/api/community/groups/${groupId}`);
      if (!response.ok) throw new Error("Failed to fetch group");
      return response.json();
    },
    enabled: !!groupId,
  });

  // 일정 목록 조회
  const { data: eventsData } = useQuery<{ events: Event[] }>({
    queryKey: ["/api/community/groups", groupId, "events"],
    queryFn: async () => {
      if (!groupId) throw new Error("Invalid group ID");
      const response = await fetch(`/api/community/groups/${groupId}/events`);
      if (!response.ok) throw new Error("Failed to fetch events");
      return response.json();
    },
    enabled: !!groupId,
  });

  // 후기 목록 조회
  const { data: postsData, isLoading: postsLoading, error: postsError } = useQuery<{ posts: any[]; pagination?: any }>({
    queryKey: ["/api/community/groups", groupId, "posts"],
    queryFn: async () => {
      if (!groupId) throw new Error("Invalid group ID");
      const response = await fetch(`/api/community/groups/${groupId}/posts`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch posts: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      console.log("[GroupDetailPage] Posts API response:", data);
      // API는 { posts: [...], pagination: {...} } 형식으로 반환
      return data;
    },
    enabled: !!groupId,
  });

  // 멤버 상태 조회
  const { data: memberStatus } = useQuery<{
    role: string;
    status: string;
  } | null>({
    queryKey: ["/api/community/groups", groupId, "member-status"],
    queryFn: async () => {
      if (!groupId || !user) return null;
      const response = await fetch(
        `/api/community/groups/${groupId}/members?role=all&status=all`
      );
      if (!response.ok) return null;
      const data = await response.json();
      const myMember = data.members?.find((m: any) => m.user_uid === user.uid);
      return myMember ? { role: myMember.role, status: myMember.status } : null;
    },
    enabled: !!groupId && !!user,
  });

  const events = eventsData?.events || [];
  const posts = postsData?.posts || [];
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // 그룹 참여
  const joinGroupMutation = useMutation({
    mutationFn: async (note?: string) => {
      if (!user || !groupId) throw new Error("로그인이 필요합니다");
      const token = await user.getIdToken();
      const response = await fetch(`/api/community/groups/${groupId}/join`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ note }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "참여에 실패했습니다");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/community/groups", groupId, "member-status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/community/groups", groupId],
      });
      toast({
        title:
          data.status === "pending"
            ? "가입 요청이 제출되었습니다"
            : "모임에 참여했습니다",
        description: data.message,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleJoin = () => {
    joinGroupMutation.mutate(undefined);
  };

  if (isLoading) {
    return (
      <PageContainer size="default">
        <div className="space-y-6">
          <div className="h-64 bg-muted rounded-lg animate-pulse" />
          <div className="h-8 bg-muted rounded w-1/2 animate-pulse" />
        </div>
      </PageContainer>
    );
  }

  if (!group) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <h3 className="text-xl font-semibold mb-2">
            모임을 찾을 수 없습니다
          </h3>
          <p className="text-muted-foreground mb-4">
            요청하신 모임이 존재하지 않거나 삭제되었습니다.
          </p>
          <Button asChild>
            <Link href="/community/groups">모임 목록으로</Link>
          </Button>
        </Card>
      </PageContainer>
    );
  }

  const isOwner = user?.uid === group.owner_uid;
  const isMember = memberStatus?.status === "active";
  const isPending = memberStatus?.status === "pending";

  return (
    <>
      <Seo
        title={`${group.title} — OZKNEWS`}
        description={group.description}
        path={`/community/groups/${group.id}`}
        keywords={[group.category_name_ko, group.city, "교민모임"]}
      />

      <div className="min-h-screen bg-background">
        {/* Cover Image */}
        {group.cover_image_url && (
          <div className="relative h-64 md:h-96 overflow-hidden">
            <img
              src={group.cover_image_url}
              alt={group.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="flex items-center gap-2 mb-2">
                <Badge
                  variant="secondary"
                  className="bg-background/90 backdrop-blur"
                >
                  {group.category_name_ko || group.category_name}
                </Badge>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                {group.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  <span>
                    {[group.suburb, group.city, group.state]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{group.member_count || 0}명</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <PageContainer size="default">
          <div className="space-y-6">
            {/* Header (when no cover image) */}
            {!group.cover_image_url && (
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" asChild>
                  <Link href="/community/groups">
                    <ArrowLeft className="w-4 h-4" />
                  </Link>
                </Button>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary">
                      {group.category_name_ko || group.category_name}
                    </Badge>
                  </div>
                  <h1 className="text-3xl font-bold">{group.title}</h1>
                </div>
              </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="about">소개</TabsTrigger>
                <TabsTrigger value="chat">대화</TabsTrigger>
                <TabsTrigger value="events">일정</TabsTrigger>
                <TabsTrigger value="posts">후기</TabsTrigger>
                <TabsTrigger value="photos">사진</TabsTrigger>
                <TabsTrigger value="members">멤버</TabsTrigger>
              </TabsList>

              {/* About Tab */}
              <TabsContent value="about" className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold mb-4">모임 소개</h2>
                    <p className="text-muted-foreground whitespace-pre-wrap">
                      {group.description}
                    </p>
                  </CardContent>
                </Card>

                {/* Next Event */}
                {group.nextEvent && (
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Calendar className="w-5 h-5 text-[#663366] dark:text-[#9966cc]" />
                        <h2 className="text-xl font-semibold">다음 일정</h2>
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold">
                          {group.nextEvent.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {new Date(
                            group.nextEvent.start_ts * 1000
                          ).toLocaleString("ko-KR")}
                        </p>
                        {group.nextEvent.location_name && (
                          <p className="text-sm text-muted-foreground">
                            📍 {group.nextEvent.location_name}
                          </p>
                        )}
                        <Button asChild variant="outline" className="mt-4">
                          <Link
                            href={`/community/groups/${groupId}/events/${group.nextEvent.id}`}
                          >
                            일정 상세보기
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Chat Tab */}
              <TabsContent value="chat">
                <GroupChatTab
                  groupId={groupId}
                  isMember={isMember}
                  isOwner={isOwner}
                  isAdmin={memberStatus?.role === "admin"}
                />
              </TabsContent>

              {/* Events Tab */}
              <TabsContent value="events" className="space-y-4">
                {events.length > 0 ? (
                  events.map((event) => (
                    <Card key={event.id} className="hover-elevate">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg mb-2">
                              {event.title}
                            </h3>
                            <div className="space-y-1 text-sm text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                <span>
                                  {new Date(
                                    event.start_ts * 1000
                                  ).toLocaleString("ko-KR")}
                                </span>
                              </div>
                              {event.location_name && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4" />
                                  <span>{event.location_name}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                <span>
                                  {event.attendance_count || 0}
                                  {event.capacity ? ` / ${event.capacity}` : ""}
                                  명
                                </span>
                              </div>
                            </div>
                          </div>
                          <Button asChild variant="outline">
                            <Link href={`/community/events/${event.id}`}>
                              일정 상세보기
                            </Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Card className="p-12 text-center">
                    <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold text-lg mb-2">
                      등록된 일정이 없습니다
                    </h3>
                    <p className="text-muted-foreground">
                      {isOwner
                        ? "첫 일정을 만들어보세요!"
                        : "일정이 등록되면 여기에 표시됩니다."}
                    </p>
                  </Card>
                )}
              </TabsContent>

              {/* Posts Tab */}
              <TabsContent value="posts" className="space-y-4">
                {/* 후기 작성 버튼 (멤버만) */}
                {isMember && (
                  <div className="flex justify-end">
                    <Button asChild>
                      <Link href={`/community/groups/${groupId}/posts/new`}>
                        <MessageCircle className="w-4 h-4 mr-2" />
                        후기 작성
                      </Link>
                    </Button>
                  </div>
                )}
                {postsLoading ? (
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-32 w-full" />
                    ))}
                  </div>
                ) : postsError ? (
                  <Card className="p-12 text-center">
                    <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-2">게시글을 불러오는 중 오류가 발생했습니다</p>
                    <p className="text-sm text-muted-foreground">
                      {postsError instanceof Error ? postsError.message : "알 수 없는 오류"}
                    </p>
                  </Card>
                ) : posts.length > 0 ? (
                  posts.map((post) => (
                    <Link key={post.id} href={`/community/posts/${post.id}`}>
                      <Card className="hover-elevate cursor-pointer">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            {post.images && post.images.length > 0 && (
                              <img
                                src={post.images[0].image_url}
                                alt={post.title || "후기"}
                                className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                              />
                            )}
                            <div className="flex-1">
                              {post.title && (
                                <h3 className="font-semibold text-lg mb-2">
                                  {post.title}
                                </h3>
                              )}
                              <p className="text-sm text-muted-foreground line-clamp-3 mb-2">
                                {post.body}
                              </p>
                              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                <span>
                                  {post.author_name || post.name || "익명"}
                                </span>
                                <span>
                                  {formatDistanceToNow(
                                    new Date(post.created_at),
                                    {
                                      addSuffix: true,
                                      locale: ko,
                                    }
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))
                ) : (
                  <Card className="p-12 text-center">
                    <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold text-lg mb-2">
                      등록된 후기가 없습니다
                    </h3>
                    <p className="text-muted-foreground">
                      {user
                        ? "첫 후기를 작성해보세요!"
                        : "로그인 후 후기를 작성할 수 있습니다."}
                    </p>
                  </Card>
                )}
              </TabsContent>

              {/* Photos Tab */}
              <TabsContent value="photos">
                <GroupPhotosTab groupId={groupId} isMember={isMember} />
              </TabsContent>

              {/* Members Tab */}
              <TabsContent value="members">
                <GroupMembersTab groupId={groupId} isOwner={isOwner} />
              </TabsContent>
            </Tabs>

            {/* Bottom Fixed Button */}
            <div className="sticky bottom-0 bg-background border-t border-border p-4 -mx-4 -mb-4">
              <div className="max-w-7xl mx-auto flex gap-3">
                {isOwner ? (
                  <Button asChild variant="outline" className="flex-1">
                    <Link href={`/community/groups/${groupId}/settings`}>
                      <Settings className="w-4 h-4 mr-2" />
                      모임 관리
                    </Link>
                  </Button>
                ) : isMember ? (
                  <Button variant="outline" className="flex-1" disabled>
                    <Users className="w-4 h-4 mr-2" />
                    멤버
                  </Button>
                ) : isPending ? (
                  <Button variant="outline" className="flex-1" disabled>
                    <UserPlus className="w-4 h-4 mr-2" />
                    승인 대기 중
                  </Button>
                ) : user ? (
                  <Button
                    className="flex-1 bg-[#c066ac] hover:bg-[#b055a0]"
                    onClick={handleJoin}
                    disabled={joinGroupMutation.isPending}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    {joinGroupMutation.isPending ? "처리 중..." : "참여하기"}
                  </Button>
                ) : (
                  <Button
                    asChild
                    className="flex-1 bg-[#c066ac] hover:bg-[#b055a0]"
                  >
                    <Link href={buildLoginPath()}>로그인 후 참여하기</Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </PageContainer>
      </div>
    </>
  );
}
